import {
  DUREE_MAX_ACCES_HEURES,
  formaterDollars,
  PLAFOND_IA_PRIVE_DEFAUT_MICRO_USD,
} from '@admin-pmvb/core'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { api, ErreurApi, lire } from '../api'
import { DemanderAcces } from './AccesTemporaires'
import { Section } from './Section'

// Vue des administrateurs sur les espaces privés (invariant I3) : l'existence de chaque espace,
// ses titulaires, sa dépense IA du mois face à son plafond (réglable ici), ses boîtes comptées
// par état. Ni adresse ni contenu : y entrer demande un accès temporaire, accordé par la
// titulaire (bouton « Demander un accès »).

export const requeteEspacesPrives = queryOptions({
  queryKey: ['espaces-prives'],
  queryFn: () => lire(api['espaces-prives'].$get()),
})

type Espace = Awaited<ReturnType<NonNullable<typeof requeteEspacesPrives.queryFn>>>[number]

/** Saisie du plafond en dollars (vide : le plafond par défaut). */
function versMicroUsd(saisie: string): number | null {
  const t = saisie.trim().replace(',', '.')
  if (t === '') return null
  const n = Number(t)
  if (!Number.isFinite(n) || n < 0) throw new Error('Plafond invalide : un montant en dollars.')
  return Math.round(n * 1_000_000)
}

function Plafond({ espace }: { espace: Espace }) {
  const client = useQueryClient()
  const [ouvert, setOuvert] = useState(false)
  const [saisie, setSaisie] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const plafond = espace.plafondEffectifMicroUsd
  if (!ouvert)
    return (
      <button
        type="button"
        className="bouton-discret"
        onClick={() => {
          setSaisie(espace.plafondIaMicroUsd === null ? '' : String(plafond / 1_000_000))
          setOuvert(true)
        }}
      >
        Régler le plafond
      </button>
    )
  return (
    <form
      className="formulaire"
      onSubmit={async (e) => {
        e.preventDefault()
        setErreur(null)
        try {
          await lire(
            api['espaces-prives'][':id'].$patch({
              param: { id: espace.id },
              json: { plafondIaMicroUsd: versMicroUsd(saisie) },
            }),
          )
          setOuvert(false)
          await client.invalidateQueries({ queryKey: ['espaces-prives'] })
        } catch (x) {
          setErreur(x instanceof Error ? x.message : 'Réglage impossible.')
        }
      }}
    >
      <label className="champ">
        <span>
          Plafond IA du mois, en dollars (vide :{' '}
          {formaterDollars(PLAFOND_IA_PRIVE_DEFAUT_MICRO_USD)})
        </span>
        <input inputMode="decimal" value={saisie} onChange={(e) => setSaisie(e.target.value)} />
      </label>
      <p className="discret">
        Au plafond, les appels IA de cet espace s’arrêtent jusqu’au mois suivant ; le reste de la
        maison n’est pas concerné.
      </p>
      {erreur && <p className="alerte">{erreur}</p>}
      <div className="boutons">
        <button type="submit" className="bouton">
          Enregistrer
        </button>
        <button type="button" className="bouton-discret" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
    </form>
  )
}

/** Les espaces privés, vus d'un administrateur : rien n'est montré s'il n'y en a aucun. */
export function EspacesPrives() {
  const espaces = useQuery(requeteEspacesPrives)
  if (espaces.error instanceof ErreurApi) return null
  if (!espaces.data || espaces.data.length === 0) return null
  return (
    <Section titre="Espaces privés">
      <p className="discret">
        Un espace privé n’est visible que de ses titulaires, administrateurs compris. Vous en voyez
        seulement le nom, les titulaires, la dépense IA du mois et les boîtes connectées, sans
        adresse ni contenu. Pour y lire, demandez un accès à sa titulaire : lecture seule,{' '}
        {DUREE_MAX_ACCES_HEURES} h au plus, qu’elle peut retirer à tout moment.
      </p>
      {espaces.data.map((e) => (
        <div key={e.id} className="ligne">
          <div className="ligne-contenu">
            <strong>{e.nom}</strong>
            <div className="discret">
              Titulaire{e.titulaires.length > 1 ? 's' : ''} :{' '}
              {e.titulaires.map((t) => t.nom).join(', ') || 'aucune'}
            </div>
            <div className="discret">
              Dépense IA du mois : {formaterDollars(e.depenseMoisMicroUsd)} sur{' '}
              {formaterDollars(e.plafondEffectifMicroUsd)}
              {e.plafondIaMicroUsd === null && ' (plafond par défaut)'}
            </div>
            <div className="discret">
              {e.boites.nombre === 0
                ? 'Aucune boîte connectée'
                : `${e.boites.nombre} boîte(s) : ${e.boites.actives} active(s), ${e.boites.aReconnecter} à reconnecter`}
            </div>
            <Plafond espace={e} />
          </div>
          {!e.titulaire && (
            <div className="boutons">
              <DemanderAcces espace={e} />
            </div>
          )}
        </div>
      ))}
    </Section>
  )
}

// Côté titulaire : la dépense IA du mois et le plafond de ses propres espaces privés, en
// lecture seule (le plafond se règle par un administrateur, depuis la vue ci-dessus).

export const requeteMesEspacesPrives = queryOptions({
  queryKey: ['espaces-prives', 'miens'],
  queryFn: () => lire(api['espaces-prives'].miens.$get()),
})

type MonEspace = Awaited<ReturnType<NonNullable<typeof requeteMesEspacesPrives.queryFn>>>[number]

function texteDepense(e: MonEspace) {
  return `${formaterDollars(e.depenseMoisMicroUsd)} sur ${formaterDollars(e.plafondIaMicroUsd)}${e.plafondParDefaut ? ' (plafond par défaut)' : ''}`
}

const NOTE_PLAFOND =
  'Au plafond, les appels IA de cet espace s’arrêtent jusqu’au mois suivant. Le plafond se règle par un administrateur.'

/**
 * Ligne « Dépense IA du mois » de la fiche d'un espace privé, pour sa titulaire ; rien pour
 * qui n'en est pas titulaire (un administrateur pendant un accès temporaire, par exemple).
 */
export function DepenseIaDeLEspace({ entiteId }: { entiteId: string }) {
  const espace = useQuery(requeteMesEspacesPrives).data?.find((e) => e.id === entiteId)
  if (!espace) return null
  return (
    <>
      <dt>Dépense IA du mois</dt>
      <dd>
        {texteDepense(espace)}
        <div className="discret">{NOTE_PLAFOND}</div>
      </dd>
    </>
  )
}

/** Réglages, onglet Système : la dépense IA de chacun de ses espaces privés. */
export function DepenseIaDeMesEspaces() {
  const espaces = useQuery(requeteMesEspacesPrives).data
  if (!espaces || espaces.length === 0) return null
  return (
    <Section titre="Dépense IA de mon espace privé">
      <dl className="details">
        {espaces.map((e) => (
          <Fragment key={e.id}>
            <dt>{e.nom}</dt>
            <dd>{texteDepense(e)}</dd>
          </Fragment>
        ))}
      </dl>
      <p className="discret">{NOTE_PLAFOND}</p>
    </Section>
  )
}
